import React, { type ComponentProps, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useScrollOffset,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { useSelector } from '@xstate5/react';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useResponsive } from '@sg/garnish';

import { useLocationSearchFlags } from '../../../../../../hooks/useLocationSearchFlags';
import { useLocationSearchVisibleLocations } from '../../../../../../hooks/useLocationSearchVisibleLocations';
import { usePredictions } from '../../../../../../hooks/usePredictions';
import { LocationSearchPredictionsList } from '../../../../../LocationSearchPredictionsList';
import { useLocationResultsScrolling } from '../../../../hooks';
import { LocationResultCardLoading } from '../../../LocationResultCard';
import { locationResultsListsV2Messages as messages } from '../../LocationResultsListsV2.messages';
import { type LocationResultsListsV2Props } from '../../LocationResultsListsV2.types';
import { LocationResultsListsV2Card } from '../LocationResultsListsV2Card';
import { LocationResultsListsV2Group } from '../LocationResultsListsV2Group';
import { LocationResultsListsV2GroupStack } from '../LocationResultsListsV2GroupStack';
import { LocationResultsListsV2ViewMap } from '../LocationResultsListsV2ViewMap';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2Locations = (props: LocationResultsListsV2LocationsProps) => {
  const {
    locationSearchActorRef,
    variation,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    onViewMapPress,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const a11ySelector = useA11ySelector('locations.results.container');

  const { shouldRenderPredictions } = usePredictions({ locationSearchActorRef });

  const { isLoadingRecentAndNearbyLocations, isLoadingLocations } = useLocationSearchFlags({
    locationSearchActorRef,
  });

  const visibleLocations = useLocationSearchVisibleLocations({ locationSearchActorRef });

  const selectedLocationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );
  const recentLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.recentLocations,
  );
  const nearbyLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.nearbyLocations,
  );
  const focusedLocationId = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.focusedLocationId,
  );
  const deliveryLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryLocation,
  );

  const { listScrollViewRef, setListItemScrollPosition, deleteListItemScrollPosition } =
    useLocationResultsScrolling({ activePinLocationId: focusedLocationId, isExpanded: true });

  const scrollOffset = useScrollOffset(listScrollViewRef);

  // ─── Derived Data ────────────────────────────────────────────────────

  const recentLocationsList = recentLocations ?? [];
  const nearbyPickupLocations = nearbyLocations?.pickup ?? [];
  const nearbyOutpostLocations = nearbyLocations?.outpost ?? [];
  const standardLocations = useMemo(() => {
    const { pickup = [], outpost = [] } = visibleLocations ?? {};

    if (selectedLocationSearchType === 'delivery') {
      return deliveryLocation ? [deliveryLocation as unknown as (typeof pickup)[number]] : [];
    }

    return selectedLocationSearchType === 'outpost' ? outpost : pickup;
  }, [deliveryLocation, visibleLocations, selectedLocationSearchType]);

  const isLoading =
    variation === 'recent-and-nearby' ? isLoadingRecentAndNearbyLocations : isLoadingLocations;

  const hasNearbyLocations =
    (selectedLocationSearchType === 'pickup' && nearbyPickupLocations.length > 0) ||
    (selectedLocationSearchType === 'outpost' && nearbyOutpostLocations.length > 0);

  const locationCardProps = useMemo<LocationResultsListsV2CardProps>(
    () => ({
      locationSearchActorRef,
      variation,
      interactedLocationRestaurantSlug,
      interactedLocationDeliveryAddressId,
      shouldShowLocationOrderChannel: false,
      onLayout: setListItemScrollPosition,
      onUmount: deleteListItemScrollPosition,
    }),
    [
      locationSearchActorRef,
      variation,
      interactedLocationRestaurantSlug,
      interactedLocationDeliveryAddressId,
      setListItemScrollPosition,
      deleteListItemScrollPosition,
    ],
  );

  const loadingPlaceholders = selectedLocationSearchType === 'delivery' ? 1 : 5;

  // ─── Styles ──────────────────────────────────────────────────────────

  const scrollTopBorderAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        scrollOffset.value,
        [0, SCROLL_TOP_BORDER_FADE_DISTANCE],
        [0, 1],
        Extrapolation.CLAMP,
      ),
    };
  });

  const contentContainerStyle = [
    styles.scrollContentContainer,
    match([styles.scrollContentContainerXS, undefined]),
  ];

  const loadingContainerStyles = [
    styles.loadingContainer,
    match([styles.loadingContainerXS, undefined]),
  ];

  // ─── Loading View ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={loadingContainerStyles}>
          <LocationResultsListsV2GroupStack>
            {Array.from({ length: loadingPlaceholders }).map((_, index) => (
              <LocationResultCardLoading key={index} index={index} />
            ))}
          </LocationResultsListsV2GroupStack>
        </View>
      </View>
    );
  }

  // ─── Predictions View ────────────────────────────────────────────────

  if (shouldRenderPredictions) {
    return (
      <LocationSearchPredictionsList
        locationSearchActorRef={locationSearchActorRef}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} {...a11ySelector.props}>
      <Animated.View
        style={[styles.scrollTopBorder, scrollTopBorderAnimatedStyles]}
        pointerEvents="none"
      />

      <Animated.ScrollView ref={listScrollViewRef} style={contentContainerStyle}>
        {onViewMapPress ? <LocationResultsListsV2ViewMap onPress={onViewMapPress} /> : null}

        {/* ─── Recent Locations ──────────────────────── */}

        {variation === 'recent-and-nearby' && recentLocationsList.length > 0 ? (
          <LocationResultsListsV2Group heading={formatMessage(messages.recentLocationsHeading)}>
            {recentLocationsList.map((location) => (
              <LocationResultsListsV2Card
                key={location.id}
                location={location}
                {...locationCardProps}
                shouldShowLocationOrderChannel={true}
              />
            ))}
          </LocationResultsListsV2Group>
        ) : null}

        {/* ─── Nearby Locations ──────────────────────── */}

        {variation === 'recent-and-nearby' && hasNearbyLocations ? (
          <LocationResultsListsV2Group heading={formatMessage(messages.nearbyLocationsHeading)}>
            {selectedLocationSearchType === 'pickup'
              ? nearbyPickupLocations.map((location) => (
                  <LocationResultsListsV2Card
                    key={location.id}
                    location={location}
                    {...locationCardProps}
                  />
                ))
              : null}

            {selectedLocationSearchType === 'outpost'
              ? nearbyOutpostLocations.map((location) => (
                  <LocationResultsListsV2Card
                    key={location.id}
                    location={location}
                    {...locationCardProps}
                  />
                ))
              : null}
          </LocationResultsListsV2Group>
        ) : null}

        {/* ─── Standard Locations ────────────────────── */}

        {variation !== 'recent-and-nearby' && standardLocations.length > 0 ? (
          <LocationResultsListsV2Group>
            {standardLocations.map((location) => (
              <LocationResultsListsV2Card
                key={location.id}
                location={location}
                {...locationCardProps}
              />
            ))}
          </LocationResultsListsV2Group>
        ) : null}
      </Animated.ScrollView>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SCROLL_TOP_BORDER_FADE_DISTANCE = theme.spacing['2'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContentContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing[4],
  },
  scrollContentContainerXS: {
    paddingTop: theme.spacing[6],
  },
  scrollTopBorder: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 1,
    borderTopWidth: 1,
    borderTopColor: theme.colors.GRAY,
  },
  loadingContainer: {
    paddingHorizontal: theme.spacing[4],
  },
  loadingContainerXS: {
    paddingTop: theme.spacing[4],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2LocationsProps = {
  onViewMapPress?: () => void;
} & Pick<
  LocationResultsListsV2Props,
  | 'locationSearchActorRef'
  | 'variation'
  | 'interactedLocationRestaurantSlug'
  | 'interactedLocationDeliveryAddressId'
>;

type LocationResultsListsV2CardProps = Omit<
  ComponentProps<typeof LocationResultsListsV2Card>,
  'location'
>;
